/**
 * Locale + currency resolution utilities (shared between admin and consumer).
 *
 * Pure helpers that translate an organisation's preference shape into the
 * BCP 47 locale tag used by `Intl.*` APIs and the matching `date-fns`
 * locale used by `react-day-picker`.
 *
 * No React imports here - `formatting-context.tsx` provides the React-side
 * provider/hook that wraps these.
 */

import type { Locale } from 'date-fns'
import {
  enGB,
  enUS,
  enAU,
  enCA,
  enIE,
  enNZ,
  enZA,
  fr,
  frCA,
  frCH,
  de,
  deAT,
  es,
  it,
  pt,
  ptBR,
  nl,
  nlBE,
  pl,
  sv,
  nb,
  da,
  fi,
  ja,
  ko,
  zhCN,
  zhTW,
  hi,
  ar,
  arSA,
  tr,
  ru,
  th,
} from 'date-fns/locale'

/** Country code → preferred language for BCP 47 fallback. */
const COUNTRY_TO_LANGUAGE: Record<string, string> = {
  GB: 'en',
  US: 'en',
  AU: 'en',
  NZ: 'en',
  CA: 'en',
  IE: 'en',
  ZA: 'en',
  FR: 'fr',
  BE: 'fr',
  CH: 'de',
  DE: 'de',
  AT: 'de',
  ES: 'es',
  MX: 'es',
  IT: 'it',
  PT: 'pt',
  BR: 'pt',
  NL: 'nl',
  PL: 'pl',
  SE: 'sv',
  NO: 'nb',
  DK: 'da',
  FI: 'fi',
  JP: 'ja',
  KR: 'ko',
  CN: 'zh',
  TW: 'zh',
  IN: 'hi',
  SA: 'ar',
  AE: 'ar',
  TR: 'tr',
  RU: 'ru',
  TH: 'th',
}

/** Sensible global defaults when no organisation preferences are loaded. */
export const DEFAULT_LOCALE = 'en-GB'
export const DEFAULT_CURRENCY = 'GBP'
export const DEFAULT_COUNTRY_CODE = 'GB'
export const DEFAULT_LANGUAGE = 'en'

/**
 * Derive a canonical BCP 47 locale tag from a country code and language.
 * Examples:
 *   countryToLocale('GB', 'en') → 'en-GB'
 *   countryToLocale('US', 'en') → 'en-US'
 *   countryToLocale('FR', 'fr') → 'fr-FR'
 *   countryToLocale('DE', '')   → 'de-DE'
 */
export function countryToLocale(countryCode: string, language: string): string {
  const country = (countryCode || DEFAULT_COUNTRY_CODE).trim().toUpperCase()
  const raw = (language || COUNTRY_TO_LANGUAGE[country] || DEFAULT_LANGUAGE).trim()
  const normalized = raw.replace(/_/g, '-')

  // If "language" is already a full locale tag (e.g. "en-US" / "pt-BR"),
  // don't append the country a second time.
  const candidate = normalized.includes('-') ? normalized : `${normalized}-${country}`

  try {
    return Intl.getCanonicalLocales(candidate)[0] ?? DEFAULT_LOCALE
  } catch {
    return DEFAULT_LOCALE
  }
}

/**
 * Map a BCP 47 locale tag to the matching `date-fns` locale object so that
 * `react-day-picker` renders weekday and month labels in the right language.
 *
 * Falls back progressively: exact `xx-YY` → language-only `xx` → en-GB.
 */
const DATE_FNS_LOCALES: Record<string, Locale> = {
  'en-GB': enGB,
  'en-US': enUS,
  'en-AU': enAU,
  'en-CA': enCA,
  'en-IE': enIE,
  'en-NZ': enNZ,
  'en-ZA': enZA,
  en: enGB,
  'fr-FR': fr,
  'fr-CA': frCA,
  'fr-CH': frCH,
  'fr-BE': fr,
  fr: fr,
  'de-DE': de,
  'de-AT': deAT,
  'de-CH': de,
  de: de,
  'es-ES': es,
  'es-MX': es,
  es: es,
  'it-IT': it,
  it: it,
  'pt-PT': pt,
  'pt-BR': ptBR,
  pt: pt,
  'nl-NL': nl,
  'nl-BE': nlBE,
  nl: nl,
  'pl-PL': pl,
  pl: pl,
  'sv-SE': sv,
  sv: sv,
  'nb-NO': nb,
  nb: nb,
  'da-DK': da,
  da: da,
  'fi-FI': fi,
  fi: fi,
  'ja-JP': ja,
  ja: ja,
  'ko-KR': ko,
  ko: ko,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  zh: zhCN,
  'hi-IN': hi,
  hi: hi,
  'ar-SA': arSA,
  'ar-AE': ar,
  ar: ar,
  'tr-TR': tr,
  tr: tr,
  'ru-RU': ru,
  ru: ru,
  'th-TH': th,
  th: th,
}

export function getDateFnsLocale(locale: string): Locale {
  if (!locale) return enGB
  const direct = DATE_FNS_LOCALES[locale]
  if (direct) return direct
  const lang = locale.split('-')[0]
  return DATE_FNS_LOCALES[lang] ?? enGB
}

/**
 * Build a localised "short date" formatter (e.g. dd/MM/yyyy in en-GB,
 * MM/dd/yyyy in en-US, dd.MM.yyyy in de-DE). Used by `DatePicker` so the
 * trigger value matches what users from that country expect.
 */
export function formatShortDate(date: Date, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date)
  } catch {
    return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date)
  }
}

// Regions that use miles for road distance (US, UK, Myanmar, Liberia);
// everyone else gets kilometres. Driven by the org's country
// preference (locale), never hardcoded per tenant.
const _IMPERIAL_REGIONS = new Set(['US', 'GB', 'MM', 'LR'])

/**
 * Format a metre distance for display in the org's locale: miles or
 * kilometres by region, number formatting by locale (e.g. "12.4 mi"
 * in en-US, "20 km" in fr-FR). The backend always returns metres (SI);
 * this is the single place the unit/locale decision is made.
 */
export function formatDistanceMeters(meters: number, locale: string): string {
  const safe = Number.isFinite(meters) && meters > 0 ? meters : 0
  let region: string | undefined
  try {
    region = new Intl.Locale(locale).maximize().region
  } catch {
    region = undefined
  }
  const imperial = region != null && _IMPERIAL_REGIONS.has(region)
  const value = imperial ? safe / 1609.344 : safe / 1000
  const unit = imperial ? 'mile' : 'kilometer'
  const maximumFractionDigits = value >= 10 ? 0 : 1
  try {
    return new Intl.NumberFormat(locale, {
      style: 'unit',
      unit,
      unitDisplay: 'short',
      maximumFractionDigits,
    }).format(value)
  } catch {
    return new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: 'unit',
      unit,
      unitDisplay: 'short',
      maximumFractionDigits,
    }).format(value)
  }
}
